import React, { useState } from 'react';
import { Bell, Download } from 'lucide-react';
import C from '../../constants/colors';
import { EMERGENCY_META } from '../../constants/meta';
import AlertCard from '../ui/AlertCard';

function formatTimeAgo(isoString) {
  if (!isoString) return 'Unknown';
  const diff = Date.now() - new Date(isoString).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60)  return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function AlertsView({ alerts, companies, sites, zones, onAcknowledge, onUpdateNotes }) {
  const [filterCompany, setFilterCompany] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [filterZone, setFilterZone] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [editingNotes, setEditingNotes] = useState({});

  const filteredSites = sites.filter(s => !filterCompany || s.companyId === filterCompany);
  const filteredZones = zones.filter(z => !filterSite || z.siteId === filterSite);

  const filtered = alerts.filter(a => {
    if (filterCompany && a.companyId !== filterCompany) return false;
    if (filterSite && a.siteId !== filterSite) return false;
    if (filterZone && a.zoneId !== filterZone) return false;
    if (filterType && a.emergencyType !== filterType) return false;
    if (filterStatus === 'pending' && a.acknowledged) return false;
    if (filterStatus === 'acknowledged' && !a.acknowledged) return false;
    if (filterFrom && new Date(a.timestamp) < new Date(filterFrom)) return false;
    if (filterTo && new Date(a.timestamp) > new Date(filterTo + 'T23:59:59')) return false;
    return true;
  }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const totalCount = filtered.length;
  const unackCount = filtered.filter(a => !a.acknowledged).length;
  const ackCount = filtered.filter(a => a.acknowledged).length;

  function handleExportCSV() {
    const headers = ['Timestamp','Employee','ID','Department','Company','Site','Zone','Type','Acknowledged','Acknowledged By','Notes'];
    const rows = filtered.map(a => [
      new Date(a.timestamp).toLocaleString(),
      a.employeeName, a.employeeIdCode, a.department,
      a.companyName, a.siteName, a.zoneName || '',
      a.emergencyType || '', a.acknowledged ? 'Yes' : 'No',
      a.acknowledgedBy || '', a.notes || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'safecheck-alerts.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  const selStyle = { background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '7px 10px', color: C.textPrimary, fontSize: '12px', fontFamily: 'inherit', outline: 'none' };
  const inpStyle = { ...selStyle, width: '140px' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.surface }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: C.textPrimary, margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Bell size={20} color={C.amber} />Alerts Log
          </h2>
          <button onClick={handleExportCSV} style={{ padding: '7px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Download size={13} />Export CSV
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filterCompany} onChange={e => { setFilterCompany(e.target.value); setFilterSite(''); setFilterZone(''); }} style={selStyle}>
            <option value=''>All Companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={filterSite} onChange={e => { setFilterSite(e.target.value); setFilterZone(''); }} style={selStyle}>
            <option value=''>All Sites</option>
            {filteredSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={filterZone} onChange={e => setFilterZone(e.target.value)} style={selStyle}>
            <option value=''>All Zones</option>
            {filteredZones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={selStyle}>
            <option value=''>All Types</option>
            {['MEDICAL','FIRE','SECURITY','OTHER'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selStyle}>
            <option value=''>All Status</option>
            <option value='pending'>Pending</option>
            <option value='acknowledged'>Acknowledged</option>
          </select>
          <input type='date' value={filterFrom} onChange={e => setFilterFrom(e.target.value)} style={inpStyle} />
          <input type='date' value={filterTo} onChange={e => setFilterTo(e.target.value)} style={inpStyle} />
          {(filterCompany || filterSite || filterZone || filterType || filterStatus || filterFrom || filterTo) && (
            <button onClick={() => { setFilterCompany(''); setFilterSite(''); setFilterZone(''); setFilterType(''); setFilterStatus(''); setFilterFrom(''); setFilterTo(''); }}
              style={{ padding: '7px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textMuted, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
        <div style={{ background: C.surfaceElevated, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {['Status','Date & Time','Employee','ID','Dept','Company','Site','Zone','Type','Emergency Contact','Acknowledged','Notes','Action'].map(h => (
                    <th key={h} style={{ padding: '10px 10px', textAlign: 'left', fontSize: '10px', fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((alert, i) => {
                  const meta = EMERGENCY_META[alert.emergencyType] || EMERGENCY_META.OTHER;
                  const isEditing = editingNotes[alert.id] !== undefined;
                  return (
                    <tr key={alert.id} style={{ borderBottom: `1px solid ${C.border}`, background: i%2===0?'transparent':'rgba(255,255,255,0.01)' }}>
                      <td style={{ padding: '10px 10px' }}>
                        {alert.acknowledged
                          ? <span style={{ fontSize: '10px', background: C.green+'22', color: C.green, padding: '2px 7px', borderRadius: '5px', fontWeight: 600 }}>ACK</span>
                          : <span style={{ fontSize: '10px', background: C.red+'22', color: C.red, padding: '2px 7px', borderRadius: '5px', fontWeight: 600, animation: 'alertPulse 2s infinite' }}>ACTIVE</span>
                        }
                      </td>
                      <td style={{ padding: '10px 10px', fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', color: C.textSecondary, whiteSpace: 'nowrap' }}>
                        {new Date(alert.timestamp).toLocaleString()}
                      </td>
                      <td style={{ padding: '10px 10px', fontWeight: 600, color: C.textPrimary, whiteSpace: 'nowrap' }}>{alert.employeeName}</td>
                      <td style={{ padding: '10px 10px', fontFamily: 'JetBrains Mono, monospace', color: C.textSecondary }}>{alert.employeeIdCode}</td>
                      <td style={{ padding: '10px 10px', color: C.textSecondary }}>{alert.department}</td>
                      <td style={{ padding: '10px 10px', color: C.textSecondary, whiteSpace: 'nowrap' }}>{alert.companyName}</td>
                      <td style={{ padding: '10px 10px', color: C.textSecondary, whiteSpace: 'nowrap' }}>{alert.siteName}</td>
                      <td style={{ padding: '10px 10px', color: C.textMuted }}>{alert.zoneName || '—'}</td>
                      <td style={{ padding: '10px 10px' }}>
                        <span style={{ fontSize: '11px', color: meta.color, fontWeight: 600 }}>{meta.label}</span>
                      </td>
                      <td style={{ padding: '10px 10px', fontSize: '11px', color: C.textSecondary, whiteSpace: 'nowrap' }}>
                        {alert.emergencyContact}<br/><span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '10px', color: C.textMuted }}>{alert.emergencyPhone}</span>
                      </td>
                      <td style={{ padding: '10px 10px', fontSize: '11px', color: C.textMuted, whiteSpace: 'nowrap' }}>
                        {alert.acknowledged ? (
                          <div>
                            <div style={{ color: C.green, fontWeight: 600 }}>{alert.acknowledgedBy}</div>
                            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '10px' }}>{alert.acknowledgedAt ? new Date(alert.acknowledgedAt).toLocaleString() : ''}</div>
                          </div>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '10px 10px', minWidth: '140px' }}>
                        {isEditing ? (
                          <input autoFocus defaultValue={editingNotes[alert.id]} onBlur={e => { onUpdateNotes(alert.id, e.target.value); setEditingNotes(p => { const n={...p}; delete n[alert.id]; return n; }); }}
                            style={{ width: '100%', background: C.surface, border: `1px solid ${C.blue}`, borderRadius: '5px', padding: '4px 8px', color: C.textPrimary, fontSize: '12px', fontFamily: 'inherit', outline: 'none' }} />
                        ) : (
                          <div onClick={() => setEditingNotes(p => ({...p,[alert.id]:alert.notes||''}))} style={{ fontSize: '11px', color: alert.notes ? C.textSecondary : C.textMuted, cursor: 'pointer', minHeight: '20px', padding: '2px 4px', borderRadius: '4px', border: '1px solid transparent' }}
                            title='Click to edit'>
                            {alert.notes || 'Add notes...'}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 10px' }}>
                        {!alert.acknowledged && (
                          <button onClick={() => onAcknowledge(alert.id)} style={{ padding: '5px 10px', borderRadius: '6px', border: `1px solid ${C.green}44`, background: C.green+'22', color: C.green, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                            Acknowledge
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={13} style={{ padding: '40px', textAlign: 'center', color: C.textMuted }}>No alerts match current filters</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Footer summary */}
      <div style={{ padding: '12px 24px', borderTop: `1px solid ${C.border}`, background: C.surface, display: 'flex', gap: '20px', fontSize: '12px', color: C.textMuted, flexShrink: 0 }}>
        <span>Total: <strong style={{ color: C.textPrimary }}>{totalCount}</strong></span>
        <span>Unacknowledged: <strong style={{ color: C.red }}>{unackCount}</strong></span>
        <span>Acknowledged: <strong style={{ color: C.green }}>{ackCount}</strong></span>
      </div>
    </div>
  );
}
